<template>
  <div :class="containerClass">
    <page-header
      :title="this.$route.params.id ? 'Edit Skills Competition' : 'Add Skills Competition'"
      :breadcrumb="breadcrumb"
      :container-class="null"
      class="mb-32pt"
    />

    <b-form class="col-md-12 px-0 page-section pt-0" @submit.prevent="onSubmit">
      <div v-if="isLoading" class="d-flex justify-content-center align-items-center " style="height: 60vh;">
        <b-spinner variant="secondary" label="Loading..." />
      </div>
      <div v-else>
        <b-form-group label="Competition Title" label-for="title" label-class="form-label">
          <b-form-input
            id="title"
            placeholder="Enter competition title"
            v-model="form.title"
            required
            :state="validation.title"
          />
          <b-form-invalid-feedback>{{ errors.title }}</b-form-invalid-feedback>
        </b-form-group>

        <b-form-group label="Host Organization" label-for="organization" label-class="form-label">
          <b-form-select
            id="organization"
            v-model="form.organization"
            :options="organizationOptions"
            required
            :state="validation.organization"
          />
          <b-form-invalid-feedback>{{ errors.organization }}</b-form-invalid-feedback>
        </b-form-group>

        <b-form-group
          label="Competition Image"
          label-for="image"
          label-class="form-label"
          class="row-align-items-center"
        >
          <b-media class="align-items-center" vertical-align="center">
            <fmv-avatar :title="true" rounded size="lg" no-link slot="aside">
              <span v-if="isImgUploading">...</span>
              <b-img :src="form.image" class="img-fluid" width="40" alt="Image" v-else-if="form.image" />
              <i class="fas fa-trophy fa-lg" v-else></i>
            </fmv-avatar>

            <image-uploader
              @image="e => setImage(e)"
              :isUploading="isImgUploading"
              :isRequired="!form.image"
              placeholder="Select Competition Image"
            />
          </b-media>
        </b-form-group>

        <div class="row">
          <div class="col-md-6">
            <b-form-group label="Status" label-class="form-label">
              <div>
                <b-form-checkbox v-model="form.is_active" switch>
                  Active
                </b-form-checkbox>
                <b-form-checkbox v-model="form.is_published" switch>
                  Published
                </b-form-checkbox>
              </div>
            </b-form-group>
          </div>
        </div>

        <b-form-group label="Short Description" label-for="short_description" label-class="form-label">
          <b-form-textarea
            id="short_description"
            placeholder="Brief description of the competition"
            v-model="form.short_description"
            required
            :rows="3"
            :state="validation.short_description"
          />
          <b-form-invalid-feedback>{{ errors.short_description }}</b-form-invalid-feedback>
        </b-form-group>

        <b-form-group label="Detailed Description" label-for="long_description" label-class="form-label">
          <editor :init="editorConfig" v-model="form.long_description" placeholder="Enter detailed description" />
        </b-form-group>

        <b-form-group label="Competition Rules" label-for="rules" label-class="form-label">
          <editor :init="editorConfig" v-model="form.rules" placeholder="Enter competition rules" />
        </b-form-group>

        <!-- Prize Information -->
        <div class="row">
          <div class="col-md-4">
            <b-form-group label="1st Place Prize" label-for="first_place_prize" label-class="form-label">
              <b-form-input
                id="first_place_prize"
                placeholder="e.g., $1000 or Scholarship"
                v-model="form.first_place_prize"
                required
              />
            </b-form-group>
          </div>
          <div class="col-md-4">
            <b-form-group label="2nd Place Prize" label-for="second_place_prize" label-class="form-label">
              <b-form-input
                id="second_place_prize"
                placeholder="e.g., $500"
                v-model="form.second_place_prize"
                required
              />
            </b-form-group>
          </div>
          <div class="col-md-4">
            <b-form-group label="3rd Place Prize" label-for="third_place_prize" label-class="form-label">
              <b-form-input id="third_place_prize" placeholder="e.g., $250" v-model="form.third_place_prize" required />
            </b-form-group>
          </div>
        </div>

        <!-- Important Dates -->
        <div class="row">
          <div class="col-md-6">
            <b-form-group label="Registration Start" label-for="registration_start_date" label-class="form-label">
              <b-form-input
                id="registration_start_date"
                v-model="form.registration_start_date"
                type="datetime-local"
                required
              />
            </b-form-group>
          </div>
          <div class="col-md-6">
            <b-form-group label="Registration End" label-for="registration_end_date" label-class="form-label">
              <b-form-input
                id="registration_end_date"
                v-model="form.registration_end_date"
                type="datetime-local"
                required
              />
            </b-form-group>
          </div>
        </div>

        <div class="row">
          <div class="col-md-6">
            <b-form-group label="Competition Start" label-for="competition_start_date" label-class="form-label">
              <b-form-input
                id="competition_start_date"
                v-model="form.competition_start_date"
                type="datetime-local"
                required
              />
            </b-form-group>
          </div>
          <div class="col-md-6">
            <b-form-group label="Competition End" label-for="competition_end_date" label-class="form-label">
              <b-form-input
                id="competition_end_date"
                v-model="form.competition_end_date"
                type="datetime-local"
                required
              />
            </b-form-group>
          </div>
        </div>

        <div class="row">
          <div class="col-md-4">
            <b-form-group label="Judging Start" label-for="judging_start_date" label-class="form-label">
              <b-form-input id="judging_start_date" v-model="form.judging_start_date" type="datetime-local" required />
            </b-form-group>
          </div>
          <div class="col-md-4">
            <b-form-group label="Judging End" label-for="judging_end_date" label-class="form-label">
              <b-form-input id="judging_end_date" v-model="form.judging_end_date" type="datetime-local" required />
            </b-form-group>
          </div>
          <div class="col-md-4">
            <b-form-group label="Winner Announcement" label-for="winner_announcement_date" label-class="form-label">
              <b-form-input
                id="winner_announcement_date"
                v-model="form.winner_announcement_date"
                type="datetime-local"
                required
              />
            </b-form-group>
          </div>
        </div>

        <!-- Participating Schools -->
        <b-form-group label="Participating Schools" label-for="participating_schools" label-class="form-label">
          <v-select
            id="participating_schools"
            class="form-control v-select-custom"
            label="name"
            v-model="form.participating_schools"
            :reduce="school => school.id"
            placeholder="Select participating schools"
            :options="availableSchools"
            :loading="areSchoolsLoading"
            multiple
          >
            <template #search="{ attributes, events }">
              <input class="vs__search" v-bind="attributes" v-on="events" />
            </template>
            <template slot="option" slot-scope="option">
              <div class="d-flex align-items-center">
                <fmv-avatar :title="true" title-class="bg-transparent" rounded no-link slot="aside" size="xs">
                  <b-img :src="option.logo_url" class="img-fluid" width="20" alt="Logo" v-if="option.logo_url" />
                  <i class="fas fa-university fa-lg" v-else></i>
                </fmv-avatar>
                <span class="ml-2">{{ option.name }}</span>
              </div>
            </template>
            <template slot="selected-option" slot-scope="option">
              {{ option.name }}
            </template>
          </v-select>
        </b-form-group>

        <!-- Competition Skillmaps -->
        <b-form-group label="Competition Skillmaps" label-for="skillmaps" label-class="form-label">
          <v-select
            id="skillmaps"
            class="form-control v-select-custom"
            label="title"
            v-model="form.skillmaps"
            :reduce="skillmap => skillmap.id"
            placeholder="Select competition skillmaps"
            :options="availableSkillmaps"
            :loading="areSkillmapsLoading"
            multiple
          >
            <template #search="{ attributes, events }">
              <input class="vs__search" v-bind="attributes" v-on="events" />
            </template>
            <template slot="option" slot-scope="option">
              <span>{{ option.title }}</span>
            </template>
            <template slot="selected-option" slot-scope="option">
              {{ option.title }}
            </template>
          </v-select>
        </b-form-group>

        <b-btn variant="primary" :disabled="isSubmitting" style="width: 150px" type="submit" class="btn-normal">
          <i v-if="isSubmitting" class="fas fa-circle-notch fa-spin"></i>
          <span v-else>{{ $route.params.id ? $t('update') : $t('add') }}</span>
        </b-btn>
      </div>
    </b-form>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import PageHeader from '@/components/Ui/PageHeader.vue';
import Editor from '@tinymce/tinymce-vue';
import Page from '@/components/Page.vue';
import ImageUploader from '@/components/ImageUploader.vue';
import { FmvAvatar } from 'fmv-avatar';
import vSelect from 'vue-select';

export default {
  components: { PageHeader, Editor, ImageUploader, FmvAvatar, 'v-select': vSelect },
  extends: Page,

  data() {
    return {
      title: '',
      isEdit: false,
      isSubmitting: false,
      isImgUploading: false,
      isLoading: this.$route.params.id ? true : false,
      form: {
        title: '',
        short_description: '',
        long_description: '',
        rules: '',
        image: '',
        organization: null,
        first_place_prize: '',
        second_place_prize: '',
        third_place_prize: '',
        registration_start_date: '',
        registration_end_date: '',
        competition_start_date: '',
        competition_end_date: '',
        judging_start_date: '',
        judging_end_date: '',
        winner_announcement_date: '',
        is_active: true,
        is_published: false,
        participating_schools: [],
        skillmaps: [],
      },

      organizations: [],
      availableSchools: [],
      availableSkillmaps: [],
      areSchoolsLoading: false,
      areSkillmapsLoading: false,

      errors: {},
      validation: {},

      editorConfig: {
        height: 300,
        menubar: 'edit view insert format tools table help',
        plugins: [
          'advlist autolink lists link charmap print preview anchor',
          'searchreplace visualblocks code fullscreen',
          'insertdatetime paste code help wordcount'
        ],
        toolbar: 'undo redo | formatselect | bold italic backcolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat | help',
        content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }'
      }
    };
  },

  computed: {
    breadcrumb() {
      return [
        { text: this.$t('home'), to: this.routes.home },
        { text: 'Skills Competitions', to: { name: 'sa-skills-competitions-list' } },
        { text: this.isEdit ? 'Edit Competition' : 'Add Competition', active: true },
      ];
    },

    organizationOptions() {
      return [
        { value: null, text: 'Select an organization...' },
        ...this.organizations.map(org => ({ value: org.id, text: org.name }))
      ];
    },
  },

  async mounted() {
    this.isEdit = !!this.$route.params.id;
    this.title = this.isEdit ? 'Edit Skills Competition' : 'Add Skills Competition';

    await this.loadInitialData();

    if (this.isEdit) {
      await this.loadCompetition();
    }
  },

  methods: {
    ...mapActions('skillsCompetitions', [
      'getCompetition',
      'createCompetition',
      'updateCompetition',
      'getCompetitionImageUploadUrl',
      'getAvailableSchools',
      'getAvailableSkillmaps'
    ]),
    ...mapActions('organization', ['getAllOrganizations']),
    ...mapActions('s3Upload', ['uploadToPresignedUrl']),

    async loadInitialData() {
      try {
        // Load organizations (without pagination like other components)
        const orgsResponse = await this.getAllOrganizations({});
        this.organizations = orgsResponse.data || [];

        // Load available schools for skills competitions
        this.areSchoolsLoading = true;
        const schoolsResponse = await this.getAvailableSchools();
        this.availableSchools = schoolsResponse.data || schoolsResponse || [];
        this.areSchoolsLoading = false;

        // Load available skillmaps for skills competitions
        this.areSkillmapsLoading = true;
        const skillmapsResponse = await this.getAvailableSkillmaps();
        this.availableSkillmaps = skillmapsResponse.data || skillmapsResponse || [];
        this.areSkillmapsLoading = false;
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: 'Error loading form data' });
        this.areSchoolsLoading = false;
        this.areSkillmapsLoading = false;
      }
    },

    async loadCompetition() {
      this.isLoading = true;
      try {
        const response = await this.getCompetition(this.$route.params.id);
        const competition = response.data || response;

        // Populate form
        Object.keys(this.form).forEach(key => {
          if (competition[key] !== undefined) {
            this.form[key] = competition[key];
          }
        });

        // Format dates for datetime-local inputs
        ['registration_start_date', 'registration_end_date', 'competition_start_date', 
         'competition_end_date', 'judging_start_date', 'judging_end_date', 'winner_announcement_date'].forEach(dateField => {
          if (competition[dateField]) {
            this.form[dateField] = new Date(competition[dateField]).toISOString().slice(0, 16);
          }
        });

        // Set organization ID (API returns object, form needs ID)
        this.form.organization = competition.organization ? competition.organization.id : null;

        // Set selected schools and skillmaps
        this.form.participating_schools = competition.participating_schools ? 
          competition.participating_schools.map(school => school.id) : [];
        this.form.skillmaps = competition.skillmaps ? 
          competition.skillmaps.map(skillmap => skillmap.id) : [];
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: 'Error loading competition' });
      }
      this.isLoading = false;
    },

    setImage(file) {
      this.uploadFile = file;
      this.uploadImage();
    },

    async uploadImage() {
      this.isImgUploading = true;

      try {
        const urlResp = await this.getCompetitionImageUploadUrl({
          file_name: this.uploadFile.name,
          content_type: this.uploadFile.type,
        });

        await this.uploadToPresignedUrl({
          url: urlResp.upload_url,
          file: this.uploadFile,
        });

        this.form.image = urlResp.upload_url.split('?')[0];
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: 'Error uploading image' });
      }

      this.isImgUploading = false;
    },



    validateForm() {
      this.errors = {};
      this.validation = {};

      // Required field validation
      const requiredFields = ['title', 'short_description', 'organization'];
      requiredFields.forEach(field => {
        if (!this.form[field]) {
          this.errors[field] = 'This field is required';
          this.validation[field] = false;
        } else {
          this.validation[field] = true;
        }
      });

      return Object.keys(this.errors).length === 0;
    },

    async onSubmit() {
      if (!this.validateForm()) {
        this.makeToast({ variant: 'danger', msg: 'Please correct the errors below' });
        return;
      }

      this.isSubmitting = true;

      try {
        // Prepare form data
        const formData = { ...this.form };

        if (this.isEdit) {
          await this.updateCompetition({
            id: this.$route.params.id,
            data: formData
          });
          this.makeToast({ variant: 'success', msg: 'Competition Updated!' });
        } else {
          await this.createCompetition(formData);
          this.makeToast({ variant: 'success', msg: 'Competition Added!' });
        }

        setTimeout(() => this.$router.push({ name: 'sa-skills-competitions-list' }), 250);
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style scoped>
.form-label {
  font-weight: 600;
  margin-bottom: 8px;
}
</style> 